import {
  CASE_REFERENCE_ANCHOR_PREFIX,
  type CaseCitationMarker,
} from '@/domain/industrialCase/industrialCaseCitation';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * ### renderIndustrialCaseCitationMarker
 *
 * Builds one inline citation marker, with the hover preview of the cited source.
 *
 * Returns a string rather than JSX because the result is spliced into authored
 * HTML from the rich-text editor before that HTML is injected.
 *
 * @param marker The citation number and the reference it points at
 * @returns The marker markup
 */
export function renderIndustrialCaseCitationMarker({
  number,
  reference,
}: CaseCitationMarker) {
  if (number === null) {
    // A marker whose reference is gone stays visible so an author can find it.
    return '<sup class="case-citation case-citation--missing">[?]</sup>';
  }

  const previewId = `${CASE_REFERENCE_ANCHOR_PREFIX}-${number}-preview`;
  const title = escapeHtml(reference?.title ?? '');
  const metadata = escapeHtml(reference?.source ?? '');
  const description = escapeHtml(reference?.description ?? '');

  return [
    '<span class="group relative ml-1 inline-flex align-super">',
    `<a class="rounded-sm px-0.5 text-[0.7em] font-semibold leading-none text-[#5f5f5a] underline decoration-[#aaa9a2] decoration-1 underline-offset-2 transition-colors duration-150 ease-out hover:text-[#ed1745]" href="#${CASE_REFERENCE_ANCHOR_PREFIX}-${number}" aria-describedby="${previewId}">[${number}]</a>`,
    `<span class="pointer-events-none absolute bottom-[calc(100%+12px)] left-1/2 z-20 hidden w-[min(320px,calc(100vw-40px))] -translate-x-1/2 translate-y-1 border border-[#cecec8] bg-[#fbfbf9] p-4 text-left opacity-0 shadow-[0_14px_34px_rgba(20,20,18,0.14)] transition-[opacity,transform] delay-100 duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100 sm:block" id="${previewId}" role="tooltip">`,
    `<strong class="block text-[14px] font-semibold leading-[1.45] tracking-[-0.01em] text-[#20201e]">[${number}] ${title}</strong>`,
    `<span class="mt-1.5 block text-[12px] font-medium leading-[1.55] text-[#767670]">${metadata}</span>`,
    description
      ? `<span class="mt-3 block text-[13px] font-normal leading-[1.65] tracking-[-0.006em] text-[#4e4e49]">${description}</span>`
      : '',
    '</span>',
    '</span>',
  ].join('');
}
